import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SidePanelService } from '@app/shared/components/side-panel/side-panel.service';
import { ManualPage } from '@app/shared/models/enums/manualPage.enum';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSort, Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, Subject, Subscription, debounceTime, distinctUntilChanged, filter, forkJoin, of, take, takeUntil } from 'rxjs';
import { Platform } from '@angular/cdk/platform';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';

import { AuthService } from '@core/auth/auth.service';
import { indicateLoading } from '@core/indicate-loading';
import { PermissionsService } from '@app/core/services/permissions.service';
import { ChannelsApi } from '@api/services/channels-api';
import { ConfirmationModalComponent } from '@shared/confirmation-modal/confirmation-modal.component';
import { SystemMessageService } from '@app/core/services/system-message.service';
import { TranslateConfigService } from '@app/core/translate-config.service';
import { Customer, CustomerApi } from '@api/index';
import { ChannelFilterFormGroup, ChannelFilterValues, ChannelListItem, ChannelParams } from './model/channel.model';
import { selectUserCustomers } from '@app/store/user/user.selectors';
import { apiCallCatchError } from '@shared/utils/utils';
import { UserRoleEnum } from '../user/model/user.model';
import { Permissions } from '@shared/models/enums/permissions.enum';

@Component({
  selector: 'flow-channel',
  templateUrl: './channel.component.html',
  styleUrls: ['./channel.component.scss'],
})
export class ChannelComponent implements OnInit, OnDestroy {
  protected destroy$ = new Subject<void>();
  displayedColumns: string[] = [
    'id',
    'name',
    'customer',
    'coordinates',
    'city',
    'address',
    'postalCode',
    'lineOfBusiness',
    'isForecastEnabled',
    'hasClosedDays',
    'weatherData',
  ];
  dataSource: MatTableDataSource<ChannelListItem>;
  customers: Customer[];
  isAdmin: boolean;
  filterForm: FormGroup<ChannelFilterFormGroup>;
  filterValues: ChannelFilterValues = {
    customers: [],
  };
  filterRequest: Subscription;
  customerId: number;
  totalCount: number = 0;
  params: ChannelParams = {
    pageNumber: 0,
    pageSize: 10,
    sortBy: 'name',
    sortDirection: 'asc',
  };
  pageSizeOptions = [5, 10, 25, 100];
  loading$ = {
    channel: new BehaviorSubject(false),
    save: new BehaviorSubject(false),
  };
  Permissions = Permissions;

  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private customerApi: CustomerApi,
    private channelsApi: ChannelsApi,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private router: Router,
    public platform: Platform,
    private permissionsService: PermissionsService,
    private dialog: MatDialog,
    private translateConfigService: TranslateConfigService,
    private systemMessageService: SystemMessageService,
    private store: Store,
    private sidePanelService: SidePanelService
  ) { }

  toggleSidePanel() {
    const config = {
      title: 'contextualModal.channels.title',
      manualKey: ManualPage.Channels,
      children: [
        {
          title: 'contextualModal.channels.whatIsThis.title',
          description: 'contextualModal.channels.whatIsThis.description',
        },
        {
          title: 'contextualModal.channels.whyIsItImportant.title',
          description: 'contextualModal.channels.whyIsItImportant.description',
        },
        {
          title: 'contextualModal.channels.howDoIUseThisFeature.title',
          description: 'contextualModal.channels.howDoIUseThisFeature.description',
        },
      ],
    };

    this.sidePanelService.openSidePanel(config);
  }

  ngOnInit(): void {
    this.initFilter();
    const user = this.authService.userData;
    this.isAdmin = user.roleId === UserRoleEnum.Admin && !user.currentCustomerId;
    if (!this.isAdmin) {
      this.customerId = user?.currentCustomerId || user?.customerId;
      this.filterForm.get('customers').setValue([this.customerId]);
      this.displayedColumns.push('actions');
    } else {
      this.displayedColumns.push('status', 'actions');
      this.getChannels(this.params);
    }
  }

  private initFilter(): void {
    this.filterForm = this.formBuilder.group({
      customers: this.formBuilder.control(null),
    });
    this.filterForm.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(50), distinctUntilChanged()).subscribe(this.onFilterChanges);
  }

  getChannels(params: ChannelParams): void {
    this.filterRequest?.unsubscribe();
    this.filterRequest = forkJoin([
      params?.customerIds && params?.customerIds.length > 0 ? apiCallCatchError(this.channelsApi.getChannelsList(params)) : of(null),
      this.isAdmin
        ? this.store.select(selectUserCustomers).pipe(
          filter(customers => customers && customers.length > 0),
          take(1)
        )
        : apiCallCatchError(this.customerApi.getCustomerById(this.customerId)),
    ])
      .pipe(takeUntil(this.destroy$), indicateLoading(this.loading$.channel))
      .subscribe(([channelsResponse, customersResponse]) => {
        const customersList = Array.isArray(customersResponse) ? customersResponse : [customersResponse];
        if (!this.customers || this.customers?.length === 0) {
          this.customers = customersList.map(c => ({ id: c.id, name: c.name }));
        }
        this.totalCount = channelsResponse ? channelsResponse.totalCount : 0;
        const channels = !channelsResponse
          ? []
          : channelsResponse?.items.map(loc => {
            const listItem = loc as ChannelListItem;
            listItem.customerName = customersList?.find(c => c.id === loc.customerId)?.name || '';
            return listItem;
          });
        this.dataSource = new MatTableDataSource(channels);
      });
  }

  onToggle(event: Event, channel: ChannelListItem, isActive: boolean): void {
    event.preventDefault();
    event.stopPropagation();
    this.channelsApi
      .editChannel({ ...channel, isActive, lineOfBusiness: channel.lineOfBusiness?.id })
      .pipe(takeUntil(this.destroy$), indicateLoading(this.loading$.save))
      .subscribe({
        complete: () => {
          this.filterForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
        },
        error: () => {
          this.filterForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });
        },
      });
  }

  onDeleteClick(event: Event, channelId: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        remove: () => this.onRemoveChannel(channelId),
        logic: true,
      },
    });
  }

  private onRemoveChannel(channelId: number) {
    this.channelsApi
      .deleteChannel(channelId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialog.closeAll();
        this.systemMessageService.success(this.translateConfigService.instant('notifications.success.channelDeleted'));
        this.getChannels(this.getParams());
      });
  }

  private onFilterChanges = (values: ChannelFilterValues): void => {
    this.filterValues = values;
    this.getChannels(this.getParams());
  };

  redirectToEditPage(channelId: number): void {
    if (this.permissionsService.hasPermissions([Permissions.UpdateChannelAdmin])) {
      this.router.navigate(['/channels', channelId]);
    }
  }

  handlePageEvent(e: PageEvent) {
    this.params.pageNumber = e.pageIndex;
    this.params.pageSize = e.pageSize;
    if (this.customerId) this.params.customerIds = [this.customerId];
    this.getChannels(this.getParams());
  }

  handleSortEvent(sort: Sort) {
    this.params = {
      ...this.params,
      pageNumber: 0,
      sortBy: sort?.active,
      sortDirection: sort.direction || (this.params.sortDirection == 'asc' ? 'desc' : 'asc') || 'asc',
    };
    this.getChannels(this.getParams());
  }

  searchChannels(params: ChannelParams): void {
    this.params = {
      ...this.params,
      ...params,
      pageNumber: 0,
      customerIds: this.filterValues.customers,
    };

    this.getChannels(this.params);
  }

  private getParams(): ChannelParams {
    return {
      ...this.params,
      customerIds: this.customerId ? [this.customerId] : this.filterValues?.customers ?? [],
    };
  }

  redirectToMaps(latitude: number, longitude: number) {
    if (latitude && longitude) {
      let url = `https://maps.google.com/?q=${latitude},${longitude}`;
      window.open(url, '_blank');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
